// src/pages/MypageMessages.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, MessageSquare, RefreshCw, Stethoscope } from 'lucide-react';
import api from '../api/axios';

interface MessageItem {
  id: number;
  reservation_id: number;
  content: string;
  created_at: string;
  read_at: string | null;
}

interface Thread {
  reservation_id: number;
  reservation_date: string | null;
  reservation_time: string | null;
  service: string | null;
  unread_count: number;
  latest_at: string;
  messages: MessageItem[];
}

function timeAgo(dateStr: string, tFn: (k: string, opts?: Record<string, unknown>) => string): string {
  const utcStr = dateStr.endsWith('Z') || dateStr.includes('+') ? dateStr : dateStr + 'Z';
  const diff = Math.floor((Date.now() - new Date(utcStr).getTime()) / 1000);
  const min = Math.floor(diff / 60);
  if (min < 1)  return tFn('doctor_messages.just_now');
  if (min < 60) return tFn('doctor_messages.minutes_ago', { n: min });
  const h = Math.floor(min / 60);
  if (h < 24)  return tFn('doctor_messages.hours_ago', { n: h });
  const d = Math.floor(h / 24);
  return tFn('doctor_messages.days_ago', { n: d });
}

const MypageMessages: React.FC = () => {
  const { t } = useTranslation();

  const [threads, setThreads] = useState<Thread[]>([]);
  const [loading, setLoading] = useState(true);
  const [openIds, setOpenIds] = useState<Set<number>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<Thread[]>('/doctor-messages');
      const fetched = res.data;
      setThreads(fetched);
      // 未読スレッドを自動展開し、同時に既読マーク
      if (fetched.some(th => th.unread_count > 0)) {
        const auto = new Set<number>();
        fetched.forEach(th => { if (th.unread_count > 0) auto.add(th.reservation_id); });
        setOpenIds(prev => prev.size === 0 ? auto : prev);
        // 自動展開したスレッドの未読メッセージを一括既読
        const unreadMsgIds = fetched
          .filter(th => th.unread_count > 0)
          .flatMap(th => th.messages.filter(m => !m.read_at).map(m => m.id));
        if (unreadMsgIds.length > 0) {
          Promise.allSettled(unreadMsgIds.map(id => api.patch(`/doctor-messages/${id}/read`)));
          // ローカルステートも即時更新
          setThreads(fetched.map(th => ({
            ...th,
            unread_count: 0,
            messages: th.messages.map(m => ({ ...m, read_at: m.read_at ?? new Date().toISOString() })),
          })));
        }
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (id: number) => {
    setOpenIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const markRead = async (thread: Thread) => {
    const unread = thread.messages.filter(m => !m.read_at);
    if (unread.length === 0) return;
    await Promise.allSettled(unread.map(m => api.patch(`/doctor-messages/${m.id}/read`)));
    setThreads(prev =>
      prev.map(th =>
        th.reservation_id === thread.reservation_id
          ? { ...th, unread_count: 0, messages: th.messages.map(m => ({ ...m, read_at: m.read_at ?? new Date().toISOString() })) }
          : th
      )
    );
  };

  const handleToggle = (thread: Thread) => {
    const isOpen = openIds.has(thread.reservation_id);
    toggle(thread.reservation_id);
    if (!isOpen) markRead(thread);
  };

  const totalUnread = threads.reduce((s, th) => s + th.unread_count, 0);

  return (
    <div className="w-full py-6 px-4 sm:px-6">
      <div className="max-w-2xl mx-auto space-y-4">

        {/* ヘッダー */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <MessageSquare size={18} className="text-coral" />
            {t('doctor_messages.title')}
            {totalUnread > 0 && (
              <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold bg-red-500 text-white rounded-full">
                {totalUnread}
              </span>
            )}
          </h2>
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition"
          >
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            {t('doctor_messages.refresh')}
          </button>
        </div>

        {/* ローディング */}
        {loading && threads.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-12">
            {t('doctor_messages.loading')}
          </p>
        )}

        {/* 空状態 */}
        {!loading && threads.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-400">
            <MessageSquare size={32} className="opacity-30" />
            <p className="text-sm">{t('doctor_messages.empty')}</p>
          </div>
        )}

        {/* スレッド一覧 */}
        {threads.map(thread => {
          const isOpen = openIds.has(thread.reservation_id);
          const hasUnread = thread.unread_count > 0;
          const lastMsg = thread.messages[thread.messages.length - 1];

          return (
            <div
              key={thread.reservation_id}
              className={`bg-white rounded-2xl border shadow-sm overflow-hidden transition-shadow hover:shadow-md ${
                hasUnread ? 'border-orange-200' : 'border-slate-200'
              }`}
            >
              {/* 未読インジケーター（左ライン） */}
              <div className={`h-1 w-full ${hasUnread ? 'bg-coral' : 'bg-transparent'}`} />

              {/* スレッドヘッダー */}
              <button
                onClick={() => handleToggle(thread)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50/70 transition text-left"
              >
                <div className="flex items-start gap-3 min-w-0">
                  {/* アイコン */}
                  <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-0.5 ${
                    hasUnread ? 'bg-coral/10' : 'bg-gray-100'
                  }`}>
                    <Stethoscope size={14} className={hasUnread ? 'text-coral' : 'text-gray-400'} />
                  </div>

                  <div className="min-w-0">
                    {/* サービス + 日時 */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {thread.service && (
                        <span className="text-[11px] font-semibold px-2 py-0.5 bg-orange-50 text-orange-600 rounded-full border border-orange-100">
                          {t(`service.${thread.service}`, thread.service)}
                        </span>
                      )}
                      <span className="text-xs text-gray-500">
                        {thread.reservation_date} {thread.reservation_time}
                      </span>
                      {hasUnread && (
                        <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-red-500 text-white rounded-full">
                          {thread.unread_count}
                        </span>
                      )}
                    </div>
                    {/* 最終メッセージプレビュー */}
                    {lastMsg && (
                      <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[220px] sm:max-w-sm">
                        {lastMsg.content}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0 ml-2">
                  <span className="text-[10px] text-gray-400 whitespace-nowrap">
                    {timeAgo(thread.latest_at, t)}
                  </span>
                  {isOpen
                    ? <ChevronUp size={14} className="text-gray-400" />
                    : <ChevronDown size={14} className="text-gray-400" />}
                </div>
              </button>

              {/* メッセージ展開 */}
              {isOpen && (
                <div className="px-4 pb-4 pt-1 space-y-3 border-t border-gray-100">
                  {thread.messages.map((msg, idx) => (
                    <div key={msg.id} className="flex items-start gap-2">
                      {/* 医師アバター（最初のメッセージのみ） */}
                      <div className="shrink-0 w-7 h-7 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center mt-1">
                        <Stethoscope size={12} className="text-blue-500" />
                      </div>

                      <div className="flex-1 min-w-0">
                        {idx === 0 && (
                          <p className="text-[10px] text-gray-400 mb-1">
                            {t('doctor_messages.attending')}
                          </p>
                        )}
                        {/* メッセージバブル */}
                        <div className="inline-block max-w-[85%] bg-gray-50 border border-gray-200 text-gray-800 text-sm px-4 py-2.5 rounded-2xl rounded-tl-sm whitespace-pre-wrap leading-relaxed">
                          {msg.content}
                        </div>
                        {/* タイムスタンプ + 既読 */}
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] text-gray-400">
                            {timeAgo(msg.created_at, t)}
                          </span>
                          <span className={`text-[10px] font-medium ${msg.read_at ? 'text-emerald-500' : 'text-amber-500'}`}>
                            {msg.read_at
                              ? t('doctor_messages.read')
                              : t('doctor_messages.unread')}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MypageMessages;
